<template>
  <div class="root">
    <div class="condition">
      <div class="title">
        司机监控
        <span v-if="list.length > 0" class="title-count">共 {{ list.length }} 辆</span>
      </div>

      <el-form ref="form" size="small" class="form" label-width="90px">
        <el-form-item label="关键字：">
          <el-input placeholder="司机编号/车牌号" v-model="inputKey" clearable style="width: 185px">
            <el-button slot="append" icon="el-icon-search" @click="query"></el-button>
          </el-input>
        </el-form-item>

        <el-form-item label="路线：">
          <el-select v-model="pathIds" placeholder="请选择路线" @change="query" multiple collapse-tags clearable>
            <el-option v-for="item in pathOpts" :key="item.value" :label="item.label" :value="item.value"></el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="汽车状态：" v-if="pathIds.length > 0">
          <el-select v-model="status" placeholder="请选择" style="width: 185px" clearable @change="query">
            <el-option v-for="item in schedulingStatus" :key="item.value" :label="item.label"
              :value="item.value"></el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="定位来源：">
          <el-select v-model="vendor" placeholder="请选择定位数据来源" @change="query" clearable>
            <el-option v-for="item in positionOpts" :key="item.value" :label="item.label"
              :value="item.value"></el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="自动刷新：">
          <el-checkbox v-model="checked">{{ checked ? "开启" : "关闭" }}</el-checkbox>
        </el-form-item>
      </el-form>

      <div class="list" v-infinite-scroll="loadMore">
        <div v-for="item in list" class="car" :class="{ active: currentCar && currentCar.driverId === item.driverId }"
          @click="showCar(item)" :key="item.telephone">
          <div class="car-top">
            <div class="car-main">
              <div class="car-no">{{ item.driver.no }}</div>
              <div class="car-name">{{ item.driver.name }}</div>
            </div>
            <div class="car-status">
              <span class="status-dot" :class="{
                s1: item.scheduling && item.scheduling.status === 1,
                s2: item.scheduling && item.scheduling.status === 2,
                s3: item.scheduling && item.scheduling.status === 3
              }"></span>
              <span>{{ getSchedulingText(item) }}</span>
            </div>
          </div>
          <div class="car-bottom">
            <div class="vehicle-no">{{ item.driver.vehicleNo }}</div>
            <el-button type="primary" size="mini" plain @click.stop="trackReplaying(item)">查看轨迹</el-button>
          </div>
        </div>
      </div>
    </div>

    <div class="map">
      <div class="map-toolbar" v-if="controlIsActive && hasTrackBaseRange">
        <div class="toolbar-range">{{ currentTrackRangeText }}</div>
        <div class="toolbar-actions">
          <el-button size="mini" type="primary" plain @click="openTrackDateDialog">选择日期范围</el-button>
          <el-button size="mini" type="primary" @click="trackQuery">查询</el-button>
        </div>
      </div>

      <div class="map-main">
        <div id="map-driver"></div>
      </div>

      <div class="capture-bottom-panel" v-if="showCapturePanel">
        <div class="capture-head">
          <div class="capture-title">监控抓拍</div>
          <span class="channel-box">
            <span>监控专查：</span>
            <el-switch v-model="channelCheck" size="small" style="margin-right: 10px" />
            <el-select v-if="channelCheck" v-model="channel" size="small" clearable @change="showChannelImgs"
              style="width: 100px">
              <el-option v-for="item in channelOpts" :key="item.value" :label="item.label" :value="item.value">
              </el-option>
            </el-select>
            <el-checkbox v-if="channelCheck" v-model="channelOnlyNonPickup">只看非接单</el-checkbox>
          </span>

          <span class="capture-count">{{ monitorShowList.length }}</span>
        </div>

        <div class="capture-slider-wrap" v-if="controlIsActive && hasTrackBaseRange">
          <el-slider v-model="captureOffset" :min="captureMin" show-stops :step="1" :max="captureMax"
            :marks="captureMarks" :format-tooltip="formatCaptureTooltip" @change="onCaptureSliderChange" />
        </div>

        <div class="capture-content">
          <el-empty v-if="!captureActiveList.length" description="暂无抓拍记录" :image-size="72" />
          <div v-else class="capture-grid">
            <div class="capture-info-inline" v-if="controlIsActive && hasTrackBaseRange">
              <div class="info-item">
                <span class="label">当前时间点：</span>
                <span class="value">{{ captureActiveTime || formatOffsetDateTime(captureOffset) }}</span>
              </div>
              <div class="info-item">
                <span class="label">状态：</span>
                <span class="value" :class="{ active: captureIsPicking }">
                  {{ captureIsPicking ? "接单中" : "非接单中" }}
                </span>
              </div>
            </div>
            <div v-for="item in captureActiveList" :key="item.id || item.url || item.channel" class="capture-item"
              @click="onCaptureItemClick(item)">
              <div class="media">
                <el-image v-if="isImageType(item.fileType) && item.url" :src="item.url"
                  :preview-src-list="captureImageList" fit="cover" />
                <video v-else-if="isVideoType(item.fileType) && item.url" :src="item.url" controls />
                <a v-else-if="item.url" :href="item.url" target="_blank" rel="noopener noreferrer">查看资源</a>
                <span v-else>无资源</span>
              </div>
              <div class="desc">
                <span>CH{{ item.channel || "-" }}</span>
                <span>{{ item.createTime }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <ChannelImgs ref="channelImgs" />

    <el-dialog title="选择轨迹日期范围" :visible.sync="trackDateDialogVisible" width="420px" append-to-body>
      <el-date-picker v-model="trackDateDialogRange" type="daterange" range-separator="至" start-placeholder="开始日期"
        end-placeholder="结束日期" value-format="yyyy-MM-dd" :picker-options="pickerOptions" style="width: 100%" />
      <div class="dialog-slider-wrap" v-if="dialogHasDateRange">
        <div class="dialog-selected-range">
          <div class="label">当前选择</div>
          <div class="value">{{ dialogSelectedRangeText }}</div>
        </div>
        <div class="dialog-slider-title">拖动滑块微调时间段</div>
        <el-slider v-model="dialogOffsetRange" range :min="0" :max="dialogSliderMax"
          :format-tooltip="formatDialogOffsetTime" />
        <div class="dialog-slider-meta">
          <span>起始：{{ formatDialogOffsetDateTime(dialogOffsetRange[0]) }}</span>
          <span>结束：{{ formatDialogOffsetDateTime(dialogOffsetRange[1]) }}</span>
        </div>
        <div class="dialog-axis-label">
          <span>{{ formatDialogMark(0) }}</span>
          <span>{{ formatDialogMark(dialogSliderMax) }}</span>
        </div>
      </div>
      <div slot="footer">
        <el-button size="small" @click="trackDateDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" @click="confirmTrackDateRange">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { FileUtil, ParamUtil } from "@/util";
import { resolveMonitorRecordType } from "@/util/monitorRecord";
import { getAllDriverLocation, getDriverMonitorRecord, getDriverTrack, getCarStopList } from "@/api";
import moment from "moment";
import ChannelImgs from "../../Order/Detail/RouteQc/ChannelImgs.vue";

const carPng = require("../../../assets/image/car_yellow.png");
const carRedPng = require("../../../assets/image/car_red.png");
const carGreenPng = require("../../../assets/image/car_green.png");
const redFlag = require("../../../assets/image/redFlag.png");
const MAX_TRACK_SPAN_HOURS = 48;

export default {
  components: {
    ChannelImgs
  },
  mounted() {
    this.initMap();
    this.query();
  },
  computed: {
    ...mapState({
      schedulingStatus: (state) => state.enumMap["SCHEDULING_STATUS"],
      pathOpts: (state) => {
        const pathList = state.pathList;
        return pathList.map((item) => {
          return {
            label: item.name,
            value: item.id
          };
        });
      }
    }),
    monitorShowList() {
      return this.monitorRecordList.filter((item) => [1].includes(item.type));
    },
    hasTrackBaseRange() {
      return Array.isArray(this.trackBaseRange) && this.trackBaseRange.length === 2;
    },
    sliderMax() {
      if (!this.hasTrackBaseRange) return 0;
      const [start, end] = this.trackBaseRange;
      return Math.max(0, moment(end).diff(moment(start), "minutes"));
    },
    currentTrackRangeText() {
      const range = this.getQueryTimeRangeFromSlider();
      if (!range.length) return "";
      return `${range[0]} 至 ${range[1]}`;
    },
    captureMin() {
      if (!Array.isArray(this.trackOffsetRange) || this.trackOffsetRange.length !== 2) return 0;
      return +this.trackOffsetRange[0] || 0;
    },
    captureMax() {
      if (!Array.isArray(this.trackOffsetRange) || this.trackOffsetRange.length !== 2) return 0;
      return +this.trackOffsetRange[1] || 0;
    },
    captureMarks() {
      if (!this.hasTrackBaseRange) return {};
      const start = this.captureMin;
      const end = this.captureMax;
      const marks = {};
      const span = Math.max(1, end - start);
      const segment = Math.min(8, span);
      for (let i = 0; i <= segment; i++) {
        const point = Math.round(start + (span * i) / segment);
        marks[point] = {
          label: this.formatOffsetTime(point),
          style: {
            fontSize: "11px",
            color: "#6f7d90"
          }
        };
      }
      return marks;
    },
    captureImageList() {
      return this.captureActiveList
        .filter((item) => item.url && this.isImageType(item.fileType))
        .map((item) => item.url);
    },
    showCapturePanel() {
      return this.hasTrackQueried && this.monitorRecordList.length > 0;
    },
    captureIsPicking() {
      return this.captureActiveList.some((item) => !!item.routeId);
    },
    dialogHasDateRange() {
      return Array.isArray(this.trackDateDialogRange) && this.trackDateDialogRange.length === 2;
    },
    dialogSliderMax() {
      if (!this.dialogHasDateRange) return 0;
      const [startDate, endDate] = this.trackDateDialogRange;
      const start = moment(startDate).startOf("day");
      const end = moment(endDate).endOf("day");
      return Math.max(0, end.diff(start, "minutes"));
    },
    dialogSelectedRangeText() {
      if (!this.dialogHasDateRange) return "";
      const [startOffset, endOffset] = this.dialogOffsetRange;
      return `${this.formatDialogOffsetDateTime(startOffset)} 至 ${this.formatDialogOffsetDateTime(endOffset)}`;
    }
  },

  watch: {
    checked: {
      handler(val) {
        if (val) {
          this.startTimer();
        } else {
          this.stopTimer();
        }
      },
      immediate: true
    },
    monitorShowList: {
      handler() {
        this.syncCaptureByOffset(this.captureOffset);
      }
    },
    trackOffsetRange: {
      handler() {
        this.captureOffset = this.captureMin;
        this.syncCaptureByOffset(this.captureOffset);
      },
      deep: true
    },
    trackDateDialogRange: {
      handler() {
        if (!this.dialogHasDateRange) return;
        this.dialogOffsetRange = [0, this.dialogSliderMax];
      },
      deep: true
    },
    channelCheck(val) {
      if (!val) {
        this.channel = null;
      }
    }
  },

  beforeDestroy() {
    this.stopTimer();
    if (this.monitorMarker && this.map) {
      this.map.remove(this.monitorMarker);
    }
    this.clearStopMarkers();
  },
  data() {
    return {
      positionOpts: [
        {
          label: "最新定位",
          value: ""
        },
        {
          label: "司机端",
          value: "driver"
        },
        {
          label: "GPS设备",
          value: "jimi"
        }
      ],
      vendor: "",
      trackBaseRange: [],
      trackOffsetRange: [0, 0],
      captureOffset: 0,
      captureActiveList: [],
      captureActiveTime: "",
      trackDateDialogVisible: false,
      trackDateDialogRange: [],
      dialogOffsetRange: [0, 0],
      pathIds: "",
      inputKey: "",
      status: null,
      currentCar: null,
      checked: false,
      list: [],
      controlIsActive: false,
      lineArr: [],
      locationStartTime: null,
      locationEndTime: null,
      monitorType: "TIMED",
      monitorRecordList: [],
      carStopList: [],
      trackLocations: [],
      stopMarkers: [],
      channelOpts: [
        { value: 1, label: "CH1" },
        { value: 2, label: "CH2" },
        { value: 3, label: "CH3" },
        { value: 4, label: "CH4" }
      ],
      channel: null,
      channelCheck: false,
      channelOnlyNonPickup: false,
      hasTrackQueried: false,

      pickerOptions: {}
    };
  },

  methods: {
    initMap() {
      this.map = new AMap.Map("map-driver", {
        resizeEnable: true,
        dragEnable: true,
        zoom: 14
      });
    },

    startTimer() {
      if (!this.timer) {
        this.timer = setInterval(() => {
          this.query();
        }, 15 * 1000);
      }
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },

    drawTrack() {
      if (this.lineArr.length) {
        this.marker = new AMap.Marker({
          map: this.map,
          position: this.lineArr[0],
          draggable: true,
          icon: new AMap.Icon({
            size: new AMap.Size(40, 40),
            image: carPng,
            imageSize: new AMap.Size(40, 40)
          }),
          offset: new AMap.Pixel(-13, -26)
        });

        // 绘制轨迹
        new AMap.Polyline({
          map: this.map,
          path: this.lineArr,
          showDir: true,
          strokeColor: "#28F", //线颜色
          strokeOpacity: 0.5, //线透明度
          strokeWeight: 6 //线宽
          // strokeStyle: "solid"  //线样式
        });

        this.map.setFitView();
      }
    },
    query() {
      const param = {
        driverNo: this.inputKey,
        status: this.status === 0 ? null : this.status,
        pathIds: this.pathIds,
        vendor: this.vendor
      };

      getAllDriverLocation(ParamUtil.filterEmptyData(param)).then((res) => {
        const { data = [] } = res;
        this.list = data;
        this.drawMap(data);
      });
    },
    trackQuery() {
      if (!this.currentCar) {
        this.$message.error("请选择要查看回放的司机");
        return;
      }
      const [startTime, endTime] = this.getQueryTimeRangeFromSlider();
      if (!startTime || !endTime) {
        this.$message.error("请先选择日期范围");
        return;
      }
      const diffHours = moment(endTime).diff(moment(startTime), "hours", true);
      if (diffHours <= 0) {
        this.$message.error("结束时间需晚于开始时间");
        return;
      }
      if (diffHours > MAX_TRACK_SPAN_HOURS) {
        this.$message.error("时间跨度不能超过2天");
        return;
      }

      const queryParam = {
        startTime,
        endTime,
        vendor: this.vendor ? this.vendor : "jimi",
        driverId: this.currentCar.driverId
      };

      Promise.all([getDriverTrack(queryParam), getDriverMonitorRecord(queryParam), getCarStopList(queryParam)]).then(
        ([trackRes, monitorRes, stopRes]) => {
          const { locations = [], locationStartTime, locationEndTime } = trackRes.data || {};
          const { monitorRecordList = [] } = monitorRes.data || {};
          const stopList = stopRes.data || [];
          const lineArr = [];
          locations.forEach((item) => {
            lineArr.push([+item[0], +item[1]]);
          });

          this.lineArr = lineArr;
          this.trackLocations = locations;
          this.carStopList = stopList;
          this.map.clearMap();
          this.drawTrack();
          this.renderStopMarkers(stopList);

          this.locationStartTime = locationStartTime;
          this.locationEndTime = locationEndTime;
          this.applyTrackDataRange(locations, locationStartTime, locationEndTime);
          this.monitorRecordList = this.formatMonitorRecordList(monitorRecordList);
          this.hasTrackQueried = true;
        }
      );
    },
    transStatus(driverType, driverStatus) {
      if (driverStatus === 2) {
        return 3;
      } else {
        if (driverType === 1) {
          return 1;
        }
        return 2;
      }
    },
    drawMap(data = []) {
      const map = this.map;
      map.clearMap();
      const markers = [];
      const markerMap = {};
      data.forEach((item) => {
        const { driver, scheduling, gpsTime, lng, lat, vendor } = item;
        const { status } = scheduling || {};
        const time = gpsTime.substr(5);
        const locationSource = vendor === "jimi" ? "GPS" : "手机";
        // const icon = new AMap.Icon({
        //     image : 'https://webapi.amap.com/images/car.png',
        //     size : new AMap.Size(52, 26),
        // });

        // 根据状态显示颜色
        let carImg = carGreenPng;
        // 异常状态
        if (status === 3) {
          carImg = carRedPng;
        } else if (status === 2) {
          carImg = carPng;
        }

        const carIcon = new AMap.Icon({
          size: new AMap.Size(40, 40), //图标大小
          image: carImg,
          imageSize: new AMap.Size(40, 40)
        });

        if (item.lat && item.lng) {
          const marker = new AMap.Marker({
            icon: carIcon,
            position: new AMap.LngLat(lng, lat),
            draggable: true,
            label: {
              content: `<div class="car-info">  ${driver.no} | ${time} | ${locationSource}</div>`,
              direction: "top"
            }
          });
          markers.push(marker);
          markerMap[item.driverId] = marker;
        }
      });
      map.add(markers);
      map.setFitView();
      this.markerMap = markerMap;
    },
    showCar(item) {
      this.currentCar = item;
      const marker = this.markerMap[item.driverId];
      if (marker && this.map) {
        this.map.setFitView([marker]);
      }
    },
    getSchedulingText(item) {
      const status = item.scheduling && item.scheduling.status;
      const target = this.schedulingStatus.find((s) => s.value === status);
      return target ? target.label : "未知状态";
    },
    trackReplaying(item) {
      this.currentCar = item;
      this.checked = false;
      const map = this.map;
      const marker = this.markerMap[item.driverId];
      if (marker) {
        map.setFitView([marker]);
      }
      this.openTrackDateDialog();
    },
    openTrackDateDialog() {
      const oldRange = this.trackBaseRange;
      this.trackDateDialogRange = this.trackBaseRange.length
        ? [
          moment(this.trackBaseRange[0]).format("YYYY-MM-DD"),
          moment(this.trackBaseRange[1]).format("YYYY-MM-DD")
        ]
        : this.getDefaultTrackBaseDateRange();
      if (oldRange.length === 2) {
        this.dialogOffsetRange = [...this.trackOffsetRange];
      } else {
        const max = this.dialogSliderMax;
        this.dialogOffsetRange = [0, max];
      }
      this.trackDateDialogVisible = true;
    },
    confirmTrackDateRange() {
      const range = this.trackDateDialogRange || [];
      if (!Array.isArray(range) || range.length !== 2) {
        this.$message.error("请选择开始和结束日期");
        return;
      }
      const [startDate, endDate] = range;
      const startTime = moment(startDate).startOf("day").format("YYYY-MM-DD HH:mm:ss");
      const endTime = moment(endDate).endOf("day").format("YYYY-MM-DD HH:mm:ss");
      const diffHours = moment(endTime).diff(moment(startTime), "hours", true);
      if (diffHours <= 0) {
        this.$message.error("结束日期需晚于开始日期");
        return;
      }
      if (diffHours > MAX_TRACK_SPAN_HOURS) {
        this.$message.error("时间跨度不能超过2天");
        return;
      }
      this.trackBaseRange = [startTime, endTime];
      const [offsetStart, offsetEnd] = this.dialogOffsetRange;
      const max = Math.max(0, moment(endTime).diff(moment(startTime), "minutes"));
      const fixStart = Math.max(0, Math.min(max, +offsetStart || 0));
      const fixEnd = Math.max(fixStart, Math.min(max, +offsetEnd || max));
      this.trackOffsetRange = [fixStart, fixEnd];
      this.trackDateDialogVisible = false;
      this.controlIsActive = true;
      this.trackQuery();
    },
    getDefaultTrackBaseDateRange() {
      const end = moment().format("YYYY-MM-DD");
      const start = end;
      return [
        start,
        end
      ];
    },
    formatOffsetTime(offsetMinute) {
      if (!this.hasTrackBaseRange) return "";
      const start = this.trackBaseRange[0];
      return moment(start).add(offsetMinute, "minutes").format("HH:mm");
    },
    formatOffsetDateTime(offsetMinute) {
      if (!this.hasTrackBaseRange) return "";
      const start = this.trackBaseRange[0];
      return moment(start).add(offsetMinute, "minutes").format("YYYY-MM-DD HH:mm:ss");
    },
    formatDialogOffsetTime(offsetMinute) {
      if (!this.dialogHasDateRange) return "";
      const [startDate] = this.trackDateDialogRange;
      return moment(startDate).startOf("day").add(offsetMinute, "minutes").format("HH:mm");
    },
    formatDialogOffsetDateTime(offsetMinute) {
      if (!this.dialogHasDateRange) return "";
      const [startDate] = this.trackDateDialogRange;
      return moment(startDate).startOf("day").add(offsetMinute, "minutes").format("YYYY-MM-DD HH:mm:ss");
    },
    formatDialogMark(offsetMinute) {
      if (!this.dialogHasDateRange) return "";
      const [startDate] = this.trackDateDialogRange;
      const dateText = moment(startDate).startOf("day").add(offsetMinute, "minutes").format("MM-DD");
      const timeText = this.formatDialogOffsetTime(offsetMinute);
      return `${dateText} ${timeText}`;
    },
    getQueryTimeRangeFromSlider() {
      if (!this.hasTrackBaseRange) return [];
      const [baseStart] = this.trackBaseRange;
      const [offsetStart, offsetEnd] = this.trackOffsetRange;
      const startTime = moment(baseStart).add(offsetStart, "minutes").format("YYYY-MM-DD HH:mm:ss");
      const endTime = moment(baseStart).add(offsetEnd, "minutes").format("YYYY-MM-DD HH:mm:ss");
      return [startTime, endTime];
    },
    onCaptureSliderChange(val) {
      this.syncCaptureByOffset(+val || this.captureMin);
    },
    syncCaptureByOffset(offset) {
      if (!this.hasTrackBaseRange) {
        this.captureActiveList = [];
        this.captureActiveTime = "";
        return;
      }
      if (!this.monitorShowList.length) {
        this.captureActiveList = [];
        this.captureActiveTime = "";
        return;
      }
      const [baseStart] = this.trackBaseRange;
      const target = moment(baseStart).add(offset, "minutes");
      let nearest = null;
      let nearestDiff = Number.MAX_SAFE_INTEGER;
      this.monitorShowList.forEach((item) => {
        const time = moment(item.createTime);
        if (!time.isValid()) return;
        const diff = Math.abs(time.diff(target, "seconds"));
        if (diff < nearestDiff) {
          nearest = item.createTime;
          nearestDiff = diff;
        }
      });
      if (!nearest) {
        this.captureActiveList = [];
        this.captureActiveTime = "";
        return;
      }
      const grouped = this.monitorShowList.filter((item) => item.createTime === nearest);
      this.captureActiveList = grouped;
      this.captureActiveTime = nearest;
      this.showTrackMarkerByTime(nearest, `当前时间：${nearest}`);
    },
    formatCaptureTooltip(val) {
      return this.formatOffsetTime(val);
    },
    applyTrackDataRange(locations = [], locationStartTime, locationEndTime) {
      if (!this.hasTrackBaseRange) return;
      const [baseStart, baseEnd] = this.trackBaseRange;
      const baseStartMoment = moment(baseStart);
      const max = Math.max(0, moment(baseEnd).diff(baseStartMoment, "minutes"));

      let startTime = locationStartTime;
      let endTime = locationEndTime;
      if ((!startTime || !endTime) && locations.length) {
        const first = locations[0];
        const last = locations[locations.length - 1];
        startTime = startTime || (first && first[2]);
        endTime = endTime || (last && last[2]);
      }
      if (!startTime || !endTime) return;

      let start = moment(startTime);
      let end = moment(endTime);
      if (!start.isValid() || !end.isValid()) return;
      if (end.isBefore(start)) {
        const tmp = start;
        start = end;
        end = tmp;
      }

      const startOffset = Math.max(0, Math.min(max, Math.floor(start.diff(baseStartMoment, "minutes", true))));
      const endOffset = Math.max(startOffset, Math.min(max, Math.ceil(end.diff(baseStartMoment, "minutes", true))));
      this.trackOffsetRange = [startOffset, endOffset];
      this.captureOffset = startOffset;
    },
    isImageType(fileType) {
      if (!fileType) return false;
      return FileUtil.isImage("." + fileType);
    },
    isVideoType(fileType) {
      if (!fileType) return false;
      return FileUtil.isVedio("." + fileType);
    },
    getMonitorType(item) {
      return resolveMonitorRecordType(item);
    },
    formatMonitorRecordList(list = []) {
      const typeMap = {
        TIMED: "定时抓拍",
        SERVICE_NODE: "服务节点抓拍",
        ALARM: "报警抓拍",
        MANUAL: "人工抓拍",
        PHONE: "接打电话",
        ADDRESS_CHANGE: "地址修改"
      };
      return list
        .map((item) => {
          const type = this.getMonitorType(item);
          return {
            ...item,
            type,
            typeLabel: typeMap[type] || "抓拍",
            createTime: item.createTime || "-"
          };
        })
        .sort((a, b) => (a.createTime || "").localeCompare(b.createTime || ""));
    },
    showMonitorMarker(point, item, autoFitView = true) {
      if (!Array.isArray(point) || point.length < 2) {
        return;
      }
      const lng = +point[0];
      const lat = +point[1];
      if (!lng || !lat || !this.map) {
        return;
      }
      if (this.monitorMarker) {
        this.map.remove(this.monitorMarker);
      }
      this.monitorMarker = new AMap.Marker({
        position: new AMap.LngLat(+lng, +lat),
        offset: new AMap.Pixel(-10, -20),
        draggable: true,
        icon: new AMap.Icon({
          size: new AMap.Size(40, 40),
          image: carPng,
          imageSize: new AMap.Size(40, 40)
        }),
        label: {
          content: `<div class="car-info">抓拍时间：${(item && item.createTime) || "-"}</div>`,
          direction: "top"
        }
      });
      this.map.add(this.monitorMarker);
      if (autoFitView) {
        this.map.setFitView([this.monitorMarker]);
      }
    },
    renderStopMarkers(stopList = [], autoFitView = false) {
      this.clearStopMarkers();
      if (!this.map || !Array.isArray(stopList) || !stopList.length) {
        return;
      }
      const icon = new AMap.Icon({
        size: new AMap.Size(32, 32),
        image: redFlag,
        imageSize: new AMap.Size(32, 32)
      });
      const markers = [];
      stopList.forEach((item) => {
        if (!item || !item.lng || !item.lat) {
          return;
        }
        const msg1 = `${item.address || "-"}，停车时长${this.formatStopDuration(item.duration)}`;
        const msg2 = item.endTime
          ? `${this.formatStopTime(item.startTime)}开始停车，${this.formatStopTime(item.endTime)}结束停车`
          : `${this.formatStopTime(item.startTime)}开始停车，尚未结束停车`;
        const marker = new AMap.Marker({
          draggable: true,
          raiseOnDrag: false,
          position: new AMap.LngLat(item.lng, item.lat),
          label: {
            direction: "top",
            content: `<div class='info stop'><div>${msg1}</div><div>${msg2}</div></div>`
          },
          anchor: "bottom-center",
          icon
        });
        markers.push(marker);
      });
      if (markers.length) {
        this.stopMarkers = markers;
        this.map.add(markers);
        if (autoFitView) {
          this.map.setFitView(markers);
        }
      }
    },
    clearStopMarkers() {
      if (this.stopMarkers && this.stopMarkers.length && this.map) {
        this.map.remove(this.stopMarkers);
      }
      this.stopMarkers = [];
    },
    showTrackMarkerByTime(time, labelPrefix, autoFitView = false) {
      if (!time || !Array.isArray(this.trackLocations) || !this.trackLocations.length || !this.map) {
        return false;
      }
      const target = moment(time);
      let selected = null;
      if (target.isValid() && this.trackLocations[0] && this.trackLocations[0].length > 2) {
        let nearestDiff = Number.MAX_SAFE_INTEGER;
        this.trackLocations.forEach((item) => {
          const pointTime = moment(item[2]);
          if (!pointTime.isValid()) return;
          const diff = Math.abs(pointTime.diff(target, "seconds"));
          if (diff < nearestDiff) {
            nearestDiff = diff;
            selected = item;
          }
        });
      } else {
        const ratio = this.captureMax > this.captureMin
          ? (this.captureOffset - this.captureMin) / (this.captureMax - this.captureMin)
          : 0;
        const index = Math.min(this.trackLocations.length - 1, Math.max(0, Math.round((this.trackLocations.length - 1) * ratio)));
        selected = this.trackLocations[index];
      }
      if (!selected || selected.length < 2) {
        return false;
      }
      const markerInfo = {
        createTime: labelPrefix || `当前时间：${time}`
      };
      this.showMonitorMarker([selected[0], selected[1]], markerInfo, autoFitView);
      return true;
    },
    onCaptureItemClick(item) {
      if (!item) return;
      const moved = this.showTrackMarkerByTime(item.createTime, `抓拍时间：${item.createTime || "-"}`, true);
      if (!moved && item.lng && item.lat) {
        this.showMonitorMarker([item.lng, item.lat], item);
      }
    },
    showChannelImgs(channel) {
      if (!this.channelCheck || !channel) {
        return;
      }
      let list = this.monitorRecordList.filter(
        (item) => item.type === 1 && item.channel === channel
      );
      if (this.channelOnlyNonPickup) {
        list = list.filter((item) => !item.routeId);
      }
      const driverId = this.currentCar && this.currentCar.driverId;
      this.$refs.channelImgs.show(list, driverId, channel - 1);
    },
    formatStopDuration(duration) {
      const seconds = +duration || 0;
      if (!seconds) return "0分钟";
      const minutes = Math.max(1, Math.round(seconds / 60));
      return `${minutes}分钟`;
    },
    formatStopTime(time) {
      if (!time) return "--:--:--";
      const value = moment(time);
      return value.isValid() ? value.format("HH:mm:ss") : "--:--:--";
    },

    loadMore() { }
  }
};
</script>

<style scoped lang="less">
.root {
  display: flex;
  height: 100%;
  background: #f4f7fb;

  .condition {
    width: 300px;
    background: #fff;
    border-right: 1px solid #e8edf3;
    display: flex;
    flex-direction: column;

    .title {
      height: 52px;
      padding: 0 14px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      color: #1f2d3d;
      font-weight: 600;
      border-bottom: 1px solid #eef2f8;
      background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
    }

    .title-count {
      color: #409eff;
      font-size: 12px;
      font-weight: 500;
    }

    .form {
      padding: 10px 12px 2px;
      border-bottom: 1px solid #eef2f8;

      .el-form-item {
        margin-bottom: 10px;
      }
    }

    .list {
      flex: 1;
      overflow: auto;
      padding: 6px;

      .car {
        border: 1px solid #e9edf5;
        border-radius: 6px;
        margin-bottom: 6px;
        padding: 6px 8px;
        cursor: pointer;
        transition: all 0.2s ease;
        background: #fff;

        &:hover {
          border-color: #c7dcff;
          box-shadow: 0 2px 8px rgba(64, 158, 255, 0.12);
        }

        &.active {
          border-color: #409eff;
          box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
          background: #f8fbff;
        }

        .car-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .car-main {
          display: flex;
          align-items: baseline;
          gap: 6px;
          min-width: 0;
        }

        .car-no {
          font-size: 13px;
          font-weight: 700;
          color: #2c3e50;
        }

        .car-name {
          font-size: 12px;
          color: #5f6b7a;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 78px;
        }

        .car-status {
          display: flex;
          align-items: center;
          gap: 3px;
          font-size: 11px;
          color: #8a97a6;
        }

        .status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #c0c4cc;

          &.s1 {
            background: #67c23a;
          }

          &.s2 {
            background: #e6a23c;
          }

          &.s3 {
            background: #f56c6c;
          }
        }

        .car-bottom {
          margin-top: 5px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .vehicle-no {
          font-size: 11px;
          color: #409eff;
          background: rgba(64, 158, 255, 0.12);
          border-radius: 999px;
          padding: 1px 7px;
        }

        /deep/ .el-button--mini {
          padding: 5px 10px;
          font-size: 12px;
        }
      }
    }
  }

  .map {
    height: 100%;
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
}

#map-driver {
  height: 100%;
  position: relative;
}

.map-toolbar {
  margin: 10px 12px 0;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #e7edf6;
  border-radius: 10px;
  box-shadow: 0 6px 18px rgba(15, 35, 95, 0.16);
  padding: 8px 12px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  .toolbar-range {
    font-size: 12px;
    color: #3d4b5d;
  }

  .toolbar-actions {
    display: flex;
    align-items: center;
    gap: 6px;
  }
}

.map-main {
  flex: 1;
  min-height: 280px;
  margin: 10px 12px 10px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e7edf6;

  #map-driver {
    height: 100%;
  }
}

.capture-bottom-panel {
  margin: 10px 12px 12px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #ebeef5;
  border-radius: 10px;
  box-shadow: 0 8px 20px rgba(15, 35, 95, 0.12);
  padding: 10px 12px;
  max-height: 38%;
  min-height: 210px;
  overflow: hidden;

  .capture-head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;

    .channel-box {
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 8px;
      margin-left: 10px;
    }
  }

  .capture-title {
    font-size: 14px;
    font-weight: 600;
    color: #253043;
  }

  .capture-count {
    margin-left: auto;
    color: #409eff;
    font-size: 12px;
  }

  .capture-slider-wrap {
    margin-bottom: 20px;

    padding: 0 20px;

    /deep/ .el-slider__marks-text {
      white-space: nowrap;
      transform: translateX(-50%) scale(0.92);
      transform-origin: center top;
    }
  }

  .capture-time {
    font-size: 12px;
    color: #4c5b70;
    margin-top: 2px;
  }

  .capture-content {
    height: 190px;
    overflow-y: auto;
  }

  .capture-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 8px;
  }

  .capture-info-inline {
    border: 1px solid #deebff;
    border-radius: 8px;
    background: #f5f9ff;
    padding: 8px 10px;
    display: flex;
    flex-direction: column;
    justify-content: center;

    .info-item {
      font-size: 12px;
      line-height: 18px;
      color: #1f2d3d;
    }

    .label {
      color: #6b7785;
    }

    .value.active {
      color: #67c23a;
      font-weight: 600;
    }
  }

  .capture-item {
    border: 1px solid #e9edf5;
    border-radius: 8px;
    padding: 6px;
    cursor: pointer;
    background: #fff;
  }

  .media {
    height: 90px;
    border-radius: 4px;
    overflow: hidden;
    background: #f7f9fc;
    display: flex;
    align-items: center;
    justify-content: center;

    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    a,
    span {
      font-size: 12px;
    }
  }

  .desc {
    margin-top: 5px;
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: #6f7d90;
    gap: 6px;
  }
}

.dialog-slider-wrap {
  margin-top: 14px;
}

.dialog-selected-range {
  background: #f5f9ff;
  border: 1px solid #deebff;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 10px;

  .label {
    font-size: 12px;
    color: #6b7785;
    margin-bottom: 4px;
  }

  .value {
    font-size: 13px;
    color: #1f2d3d;
    font-weight: 500;
    line-height: 1.5;
    word-break: break-all;
  }
}

.dialog-slider-title {
  font-size: 12px;
  color: #3d4b5d;
  margin-bottom: 8px;
}

.dialog-slider-meta {
  margin-top: 8px;
  font-size: 12px;
  color: #4c5b70;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dialog-axis-label {
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #8a97a6;
}

/deep/ .car-info {
  background-color: rgba(0, 0, 0, 0.65);
  color: white;
  padding: 5px 8px;
  border-radius: 3px;
  box-shadow: rgba(0, 0, 0, 0.5) 0px 2px 6px 0px;
  display: flex;
  align-items: center;
}

/deep/ .info.stop {
  padding: 6px 8px;
  border: 1px solid #f3d19e;
  border-radius: 4px;
  background: #fdf6ec;
  color: #606266;
  font-size: 12px;
  line-height: 18px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

@media screen and (max-width: 1500px) {
  .map-toolbar .toolbar-actions {
    gap: 4px;
  }

  .capture-bottom-panel .capture-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
